import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Switch } from '../ui/switch';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Checkbox } from '../ui/checkbox';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { toast } from 'sonner';
import { copyToClipboard } from '../../utils/clipboard';
import {
  Search,
  Plus,
  Edit,
  Trash2,
  Eye,
  UserPlus,
  Link2,
  Copy,
  Users,
  Star,
  Loader2,
  AlertCircle,
} from 'lucide-react';
import {
  apiService,
  RegistrationProfile,
  RegistrationProfileModel,
  RegistrationProfileSttProvider,
  RegistrationProfileTtsProvider,
  CreateRegistrationProfileRequest,
  SubscriptionPlan,
  LLMModel,
  PromptTemplate,
  SpeechToTextProvider,
  TextToSpeechProvider,
} from '../../services/api';

// ── Provider-grouped model entry ─────────────────────────────────────────────

interface LlmProviderGroup {
  providerId: string;
  providerName: string;
  dataResidency?: string;
  models: LLMModel[];
}

function buildLlmGroups(models: LLMModel[]): LlmProviderGroup[] {
  const groupMap = new Map<string, LlmProviderGroup>();
  for (const model of models) {
    const key = model.provider?.id ?? model.providerId;
    const providerName =
      model.provider?.displayName ?? model.provider?.name ?? 'Unbekannter Provider';
    const dataResidency = model.provider?.dataResidency;
    const existing = groupMap.get(key);
    if (existing) {
      existing.models.push(model);
    } else {
      groupMap.set(key, { providerId: key, providerName, dataResidency, models: [model] });
    }
  }
  return [...groupMap.values()].sort((a, b) =>
    a.providerName.localeCompare(b.providerName, 'de'),
  );
}

// ── Local form data ───────────────────────────────────────────────────────────

interface ProfileFormData {
  name: string;
  description: string;
  subscriptionPlanId: string;
  selectedModels: string[];
  defaultModelId: string;
  selectedSTTProviders: string[];
  defaultSttProviderId: string;
  selectedTTSProviders: string[];
  defaultTtsProviderId: string;
  selectedTemplates: string[];
  platformConfig: {
    showChatbots: boolean;
    showVoicebots: boolean;
    showKnowledge: boolean;
    showTemplates: boolean;
    showTools: boolean;
    showTestScenarios: boolean;
    showUserManagement: boolean;
    showVoiceFunctionality: boolean;
    showSpeechToText: boolean;
    showTimeoutsConfig: boolean;
    showFeedbackFunctionality: boolean;
    showApiKeysManagement: boolean;
  };
  isActive: boolean;
  isDefault: boolean;
  maxRegistrations: number | undefined;
}

const EMPTY_FORM: ProfileFormData = {
  name: '',
  description: '',
  subscriptionPlanId: '',
  selectedModels: [],
  defaultModelId: '',
  selectedSTTProviders: [],
  defaultSttProviderId: '',
  selectedTTSProviders: [],
  defaultTtsProviderId: '',
  selectedTemplates: [],
  platformConfig: {
    showChatbots: true,
    showVoicebots: true,
    showKnowledge: true,
    showTemplates: true,
    showTools: true,
    showTestScenarios: true,
    showUserManagement: true,
    showVoiceFunctionality: true,
    showSpeechToText: true,
    showTimeoutsConfig: true,
    showFeedbackFunctionality: true,
    showApiKeysManagement: true,
  },
  isActive: true,
  isDefault: false,
  maxRegistrations: undefined,
};

// ── Local representation of a loaded profile ─────────────────────────────────

interface LocalRegistrationProfile extends RegistrationProfile {
  selectedModels: string[];
  defaultModelId: string;
  selectedSTTProviders: string[];
  defaultSttProviderId: string;
  selectedTTSProviders: string[];
  defaultTtsProviderId: string;
  selectedTemplates: string[];
  registrationLink: string;
  usageCount: number;
  maxRegistrations: number | undefined;
}

function convertToLocal(apiProfile: RegistrationProfile): LocalRegistrationProfile {
  const defaultModel =
    apiProfile.profileModels?.find((pm: RegistrationProfileModel) => pm.isDefault === true);
  const defaultStt =
    apiProfile.profileSttProviders?.find((p: RegistrationProfileSttProvider) => p.isDefault === true);
  const defaultTts =
    apiProfile.profileTtsProviders?.find((p: RegistrationProfileTtsProvider) => p.isDefault === true);

  return {
    ...apiProfile,
    selectedModels: apiProfile.profileModels?.map((pm: RegistrationProfileModel) => pm.modelId) ?? [],
    defaultModelId: defaultModel?.modelId ?? '',
    selectedSTTProviders:
      apiProfile.profileSttProviders?.map((p: RegistrationProfileSttProvider) => p.providerId) ?? [],
    defaultSttProviderId: defaultStt?.providerId ?? '',
    selectedTTSProviders:
      apiProfile.profileTtsProviders?.map((p: RegistrationProfileTtsProvider) => p.providerId) ?? [],
    defaultTtsProviderId: defaultTts?.providerId ?? '',
    selectedTemplates: apiProfile.profileTemplates?.map((pt) => pt.templateId) ?? [],
    registrationLink: `${window.location.origin}/register/${apiProfile.id}`,
    usageCount: 0,
    maxRegistrations: undefined,
  };
}

// ── Page component ────────────────────────────────────────────────────────────

export function RegistrationProfilesPage() {
  const [profiles, setProfiles] = useState<LocalRegistrationProfile[]>([]);
  const [subscriptionPlans, setSubscriptionPlans] = useState<SubscriptionPlan[]>([]);
  const [models, setModels] = useState<LLMModel[]>([]);
  const [sttProviders, setSttProviders] = useState<SpeechToTextProvider[]>([]);
  const [ttsProviders, setTtsProviders] = useState<TextToSpeechProvider[]>([]);
  const [templates, setTemplates] = useState<PromptTemplate[]>([]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [viewingProfile, setViewingProfile] = useState<LocalRegistrationProfile | null>(null);
  const [editingProfile, setEditingProfile] = useState<LocalRegistrationProfile | null>(null);
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState<ProfileFormData>(EMPTY_FORM);

  // ── Data loading ────────────────────────────────────────────────────────────

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [profilesRes, plansRes, modelsRes, sttRes, ttsRes, templatesRes] = await Promise.all([
        apiService.getRegistrationProfiles(),
        apiService.getSubscriptionPlans(),
        apiService.getLlmModels({ includeInactive: false }),
        apiService.getSpeechToTextProviders(),
        apiService.getTextToSpeechProviders(),
        apiService.getPromptTemplates({ isSystemTemplate: true, isActive: true }),
      ]);
      setProfiles(profilesRes.profiles.map(convertToLocal));
      setSubscriptionPlans(plansRes);
      setModels(modelsRes);
      setSttProviders(sttRes);
      setTtsProviders(ttsRes);
      setTemplates(templatesRes.templates);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Fehler beim Laden der Daten');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  // ── Helpers ─────────────────────────────────────────────────────────────────

  const llmGroups = buildLlmGroups(models);

  function toggleModel(modelId: string) {
    setFormData((prev) => {
      const next = prev.selectedModels.includes(modelId)
        ? prev.selectedModels.filter((id) => id !== modelId)
        : [...prev.selectedModels, modelId];
      // Clear default if deselected
      const newDefault = next.includes(prev.defaultModelId) ? prev.defaultModelId : '';
      return { ...prev, selectedModels: next, defaultModelId: newDefault };
    });
  }

  function toggleStt(providerId: string) {
    setFormData((prev) => {
      const next = prev.selectedSTTProviders.includes(providerId)
        ? prev.selectedSTTProviders.filter((id) => id !== providerId)
        : [...prev.selectedSTTProviders, providerId];
      const newDefault = next.includes(prev.defaultSttProviderId) ? prev.defaultSttProviderId : '';
      return { ...prev, selectedSTTProviders: next, defaultSttProviderId: newDefault };
    });
  }

  function toggleTts(providerId: string) {
    setFormData((prev) => {
      const next = prev.selectedTTSProviders.includes(providerId)
        ? prev.selectedTTSProviders.filter((id) => id !== providerId)
        : [...prev.selectedTTSProviders, providerId];
      const newDefault = next.includes(prev.defaultTtsProviderId) ? prev.defaultTtsProviderId : '';
      return { ...prev, selectedTTSProviders: next, defaultTtsProviderId: newDefault };
    });
  }

  function toggleTemplate(templateId: string) {
    setFormData((prev) => ({
      ...prev,
      selectedTemplates: prev.selectedTemplates.includes(templateId)
        ? prev.selectedTemplates.filter((id) => id !== templateId)
        : [...prev.selectedTemplates, templateId],
    }));
  }

  function updatePlatformConfig(key: keyof ProfileFormData['platformConfig'], value: boolean) {
    setFormData((prev) => ({
      ...prev,
      platformConfig: { ...prev.platformConfig, [key]: value },
    }));
  }

  // ── Dialog open/close ──────────────────────────────────────────────────────

  function openCreate() {
    setEditingProfile(null);
    setViewingProfile(null);
    setFormData(EMPTY_FORM);
    setIsDialogOpen(true);
  }

  function openEdit(profile: LocalRegistrationProfile) {
    setEditingProfile(profile);
    setViewingProfile(null);
    setFormData({
      name: profile.name,
      description: profile.description ?? '',
      subscriptionPlanId: profile.subscriptionPlanId,
      selectedModels: [...profile.selectedModels],
      defaultModelId: profile.defaultModelId,
      selectedSTTProviders: [...profile.selectedSTTProviders],
      defaultSttProviderId: profile.defaultSttProviderId,
      selectedTTSProviders: [...profile.selectedTTSProviders],
      defaultTtsProviderId: profile.defaultTtsProviderId,
      selectedTemplates: [...profile.selectedTemplates],
      platformConfig: { ...profile.platformConfig },
      isActive: profile.isActive,
      isDefault: profile.isDefault,
      maxRegistrations: profile.maxRegistrations,
    });
    setIsDialogOpen(true);
  }

  function openView(profile: LocalRegistrationProfile) {
    setViewingProfile(profile);
    setEditingProfile(null);
    setIsDialogOpen(true);
  }

  function closeDialog() {
    setIsDialogOpen(false);
    setEditingProfile(null);
    setViewingProfile(null);
    setFormData(EMPTY_FORM);
  }

  // ── Save ────────────────────────────────────────────────────────────────────

  async function handleSave() {
    if (!formData.name.trim()) {
      toast.error('Pflichtfeld fehlt', { description: 'Bitte einen Profilnamen eingeben.' });
      return;
    }
    if (!formData.subscriptionPlanId) {
      toast.error('Pflichtfeld fehlt', { description: 'Bitte einen Abonnement-Plan wählen.' });
      return;
    }

    const requestData: CreateRegistrationProfileRequest = {
      name: formData.name,
      description: formData.description || undefined,
      subscriptionPlanId: formData.subscriptionPlanId,
      platformConfig: formData.platformConfig,
      isActive: formData.isActive,
      isDefault: formData.isDefault,
      sortOrder: profiles.length,
      models: formData.selectedModels.map((modelId) => ({
        modelId,
        isEnabled: true,
        isDefault: formData.defaultModelId === modelId,
      })),
      sttProviders: formData.selectedSTTProviders.map((providerId) => ({
        providerId,
        isEnabled: true,
        isDefault: formData.defaultSttProviderId === providerId,
      })),
      ttsProviders: formData.selectedTTSProviders.map((providerId) => ({
        providerId,
        isEnabled: true,
        isDefault: formData.defaultTtsProviderId === providerId,
      })),
      templates: formData.selectedTemplates.map((templateId) => ({
        templateId,
        isEnabled: true,
      })),
    };

    setSaving(true);
    try {
      if (!editingProfile) {
        const newProfile = await apiService.createRegistrationProfile(requestData);
        setProfiles((prev) => [...prev, convertToLocal(newProfile)]);
        toast.success('Profil erstellt');
      } else {
        const updated = await apiService.updateRegistrationProfileFull(
          editingProfile.id,
          requestData,
        );
        setProfiles((prev) =>
          prev.map((p) => (p.id === editingProfile.id ? convertToLocal(updated) : p)),
        );
        toast.success('Profil gespeichert');
      }
      closeDialog();
    } catch (err) {
      toast.error(`Fehler beim ${editingProfile ? 'Speichern' : 'Erstellen'}`, {
        description: err instanceof Error ? err.message : 'Unbekannter Fehler',
      });
    } finally {
      setSaving(false);
    }
  }

  // ── Profile actions ─────────────────────────────────────────────────────────

  async function handleDelete(id: string) {
    if (!confirm('Profil wirklich löschen?')) return;
    try {
      await apiService.deleteRegistrationProfile(id);
      setProfiles((prev) => prev.filter((p) => p.id !== id));
      toast.success('Profil gelöscht');
    } catch (err) {
      toast.error('Fehler beim Löschen', {
        description: err instanceof Error ? err.message : 'Unbekannter Fehler',
      });
    }
  }

  async function toggleActive(id: string) {
    const profile = profiles.find((p) => p.id === id);
    if (!profile) return;
    try {
      const updated = await apiService.updateRegistrationProfile(id, {
        isActive: !profile.isActive,
      });
      setProfiles((prev) => prev.map((p) => (p.id === id ? convertToLocal(updated) : p)));
    } catch (err) {
      toast.error('Fehler beim Statuswechsel', {
        description: err instanceof Error ? err.message : 'Unbekannter Fehler',
      });
    }
  }

  async function toggleDefault(id: string) {
    const profile = profiles.find((p) => p.id === id);
    if (!profile) return;
    try {
      await apiService.updateRegistrationProfile(id, { isDefault: !profile.isDefault });
      const updatedProfiles = await apiService.getRegistrationProfiles();
      setProfiles(updatedProfiles.profiles.map(convertToLocal));
    } catch (err) {
      toast.error('Fehler beim Ändern des Standards', {
        description: err instanceof Error ? err.message : 'Unbekannter Fehler',
      });
    }
  }

  // ── Loading / Error ─────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-gray-500 mx-auto mb-4" />
          <p className="text-gray-600">Lade Registrierungsprofile...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-8">
        <div className="bg-red-50 border border-red-200 rounded-md p-4 flex items-start space-x-3">
          <AlertCircle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
          <div>
            <h3 className="text-red-800 font-medium mb-1">Fehler beim Laden</h3>
            <p className="text-red-600 text-sm">{error}</p>
            <button
              onClick={() => void loadData()}
              className="mt-3 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 text-sm"
            >
              Seite neu laden
            </button>
          </div>
        </div>
      </div>
    );
  }

  const filteredProfiles = profiles.filter(
    (p) =>
      p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (p.description ?? '').toLowerCase().includes(searchTerm.toLowerCase()),
  );

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Registrierungsprofile</h1>
          <p className="text-gray-600">Verwaltung der Self-Service Registrierungsprofile</p>
        </div>
        <Button onClick={openCreate}>
          <Plus className="w-4 h-4 mr-2" />
          Neues Profil
        </Button>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
        <Input
          placeholder="Profile suchen..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10"
        />
      </div>

      {/* Profile grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredProfiles.map((profile) => (
          <Card key={profile.id} className="hover:shadow-md transition-shadow">
            <CardHeader className="pb-3">
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center space-x-2 mb-2">
                    <UserPlus className="w-5 h-5 text-blue-600 flex-shrink-0" />
                    <CardTitle className="text-lg truncate">{profile.name}</CardTitle>
                  </div>
                  <div className="flex flex-wrap items-center gap-1.5 mb-2">
                    <Badge variant="outline" className="text-xs">
                      {profile.subscriptionPlan?.name ?? 'Kein Plan'}
                    </Badge>
                    <Badge variant={profile.isActive ? 'default' : 'secondary'} className="text-xs">
                      {profile.isActive ? 'Aktiv' : 'Inaktiv'}
                    </Badge>
                    {profile.isDefault && (
                      <Badge className="bg-yellow-500 hover:bg-yellow-600 text-xs">
                        <Star className="w-3 h-3 mr-1" />
                        Standard
                      </Badge>
                    )}
                  </div>
                </div>
                <Switch
                  checked={profile.isActive}
                  onCheckedChange={() => void toggleActive(profile.id)}
                  aria-label="Profil aktiv/inaktiv"
                />
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <CardDescription className="text-sm text-gray-600">
                  {profile.description ?? 'Keine Beschreibung'}
                </CardDescription>

                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <div className="font-medium text-gray-700">Modelle</div>
                    <div className="text-gray-600">
                      {profile.selectedModels.length}
                      {profile.defaultModelId && (
                        <span className="text-xs text-violet-600 ml-1">
                          (1 Default)
                        </span>
                      )}
                    </div>
                  </div>
                  <div>
                    <div className="font-medium text-gray-700">Templates</div>
                    <div className="text-gray-600">{profile.selectedTemplates.length}</div>
                  </div>
                  <div>
                    <div className="font-medium text-gray-700">STT</div>
                    <div className="text-gray-600">
                      {profile.selectedSTTProviders.length}
                      {profile.defaultSttProviderId && (
                        <span className="text-xs text-violet-600 ml-1">(1 Default)</span>
                      )}
                    </div>
                  </div>
                  <div>
                    <div className="font-medium text-gray-700">TTS</div>
                    <div className="text-gray-600">
                      {profile.selectedTTSProviders.length}
                      {profile.defaultTtsProviderId && (
                        <span className="text-xs text-violet-600 ml-1">(1 Default)</span>
                      )}
                    </div>
                  </div>
                </div>

                <div className="flex items-center space-x-1 text-xs text-gray-500">
                  <Users className="w-3.5 h-3.5" />
                  <span>{profile.usageCount} Registrierungen</span>
                </div>

                <div className="text-xs text-gray-400">
                  Erstellt: {new Date(profile.createdAt).toLocaleDateString('de-DE')}
                </div>

                <div className="flex items-center space-x-1 pt-1">
                  <Button variant="ghost" size="sm" onClick={() => openView(profile)} title="Ansehen">
                    <Eye className="w-4 h-4" />
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => openEdit(profile)} title="Bearbeiten">
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button
                    variant={profile.isDefault ? 'default' : 'ghost'}
                    size="sm"
                    onClick={() => void toggleDefault(profile.id)}
                    title={profile.isDefault ? 'Standard entfernen' : 'Als Standard setzen'}
                    className={profile.isDefault ? 'bg-yellow-500 hover:bg-yellow-600 text-white' : ''}
                  >
                    <Star className={`w-4 h-4 ${profile.isDefault ? 'fill-current' : ''}`} />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      void copyToClipboard(profile.registrationLink, 'Link');
                    }}
                    title="Registrierungslink kopieren"
                  >
                    <Link2 className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => void handleDelete(profile.id)}
                    className="text-red-600 hover:text-red-700"
                    title="Löschen"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {filteredProfiles.length === 0 && (
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-gray-500">Keine Registrierungsprofile gefunden.</p>
            <Button className="mt-4" onClick={openCreate}>
              Erstes Profil erstellen
            </Button>
          </CardContent>
        </Card>
      )}

      {/* ── Dialog ── */}
      <Dialog open={isDialogOpen} onOpenChange={closeDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {viewingProfile
                ? 'Profil Details'
                : editingProfile
                  ? 'Profil bearbeiten'
                  : 'Neues Registrierungsprofil erstellen'}
            </DialogTitle>
            <DialogDescription>
              {viewingProfile
                ? 'Detaillierte Ansicht des Registrierungsprofils'
                : 'Konfiguriere ein Registrierungsprofil für Self-Service Registrierungen'}
            </DialogDescription>
          </DialogHeader>

          {viewingProfile ? (
            <ViewProfileContent
              profile={viewingProfile}
              models={models}
              sttProviders={sttProviders}
              ttsProviders={ttsProviders}
              templates={templates}
              onEdit={() => openEdit(viewingProfile)}
              onCopyLink={() => {
                void copyToClipboard(viewingProfile.registrationLink, 'Link');
              }}
              onClose={closeDialog}
            />
          ) : (
            <EditProfileForm
              formData={formData}
              setFormData={setFormData}
              models={models}
              llmGroups={llmGroups}
              sttProviders={sttProviders}
              ttsProviders={ttsProviders}
              templates={templates}
              subscriptionPlans={subscriptionPlans}
              saving={saving}
              onToggleModel={toggleModel}
              onToggleStt={toggleStt}
              onToggleTts={toggleTts}
              onToggleTemplate={toggleTemplate}
              onUpdatePlatformConfig={updatePlatformConfig}
              onSave={() => void handleSave()}
              onCancel={closeDialog}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ── View profile dialog content ───────────────────────────────────────────────

interface ViewProfileContentProps {
  profile: LocalRegistrationProfile;
  models: LLMModel[];
  sttProviders: SpeechToTextProvider[];
  ttsProviders: TextToSpeechProvider[];
  templates: PromptTemplate[];
  onEdit: () => void;
  onCopyLink: () => void;
  onClose: () => void;
}

function ViewProfileContent({
  profile,
  models,
  sttProviders,
  ttsProviders,
  templates,
  onEdit,
  onCopyLink,
  onClose,
}: ViewProfileContentProps) {
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-6">
        <div className="space-y-4">
          <div>
            <h4 className="text-sm font-medium text-gray-700 mb-2">Profil Information</h4>
            <div className="space-y-1.5 text-sm">
              <div><strong>Name:</strong> {profile.name}</div>
              <div><strong>Beschreibung:</strong> {profile.description ?? '—'}</div>
              <div><strong>Abonnement:</strong> {profile.subscriptionPlan?.name ?? '—'}</div>
              <div><strong>Status:</strong> {profile.isActive ? 'Aktiv' : 'Inaktiv'}</div>
              <div className="flex items-center gap-2">
                <strong>Standard:</strong>
                {profile.isDefault ? (
                  <Badge className="bg-yellow-500 text-white text-xs">
                    <Star className="w-3 h-3 mr-1 fill-current" />
                    Ja
                  </Badge>
                ) : (
                  <span className="text-gray-600">Nein</span>
                )}
              </div>
            </div>
          </div>
          <div>
            <h4 className="text-sm font-medium text-gray-700 mb-2">Registrierungslink</h4>
            <div className="flex items-center space-x-2">
              <code className="flex-1 text-xs bg-gray-100 p-2 rounded break-all">
                {profile.registrationLink}
              </code>
              <Button variant="outline" size="sm" onClick={onCopyLink}>
                <Copy className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>

        <div>
          <h4 className="text-sm font-medium text-gray-700 mb-2">Platform Konfiguration</h4>
          <div className="grid grid-cols-2 gap-1.5 text-sm">
            {Object.entries(profile.platformConfig).map(([key, value]) => (
              <div key={key} className="flex items-center justify-between">
                <span className="text-gray-600 capitalize">
                  {key.replace(/([A-Z])/g, ' $1').replace('show', '').trim()}
                </span>
                <Badge variant={value ? 'default' : 'secondary'} className="text-xs ml-1">
                  {value ? 'An' : 'Aus'}
                </Badge>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4">
        <div>
          <h4 className="text-sm font-medium text-gray-700 mb-2">
            KI-Modelle ({profile.selectedModels.length})
          </h4>
          <div className="space-y-1">
            {profile.selectedModels.map((modelId) => {
              const model = models.find((m) => m.id === modelId);
              return (
                <div key={modelId} className="flex items-center gap-1">
                  <div className="text-xs bg-blue-50 text-blue-700 px-2 py-1 rounded flex-1">
                    {model?.displayName ?? modelId}
                  </div>
                  {profile.defaultModelId === modelId && (
                    <Badge className="text-xs bg-violet-100 text-violet-700 border-violet-200">
                      Default
                    </Badge>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        <div>
          <h4 className="text-sm font-medium text-gray-700 mb-2">
            STT Provider ({profile.selectedSTTProviders.length})
          </h4>
          <div className="space-y-1">
            {profile.selectedSTTProviders.map((providerId) => {
              const provider = sttProviders.find((p) => p.id === providerId);
              return (
                <div key={providerId} className="flex items-center gap-1">
                  <div className="text-xs bg-green-50 text-green-700 px-2 py-1 rounded flex-1">
                    {provider?.displayName ?? providerId}
                  </div>
                  {profile.defaultSttProviderId === providerId && (
                    <Badge className="text-xs bg-violet-100 text-violet-700 border-violet-200">
                      Default
                    </Badge>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        <div>
          <h4 className="text-sm font-medium text-gray-700 mb-2">
            TTS Provider ({profile.selectedTTSProviders.length})
          </h4>
          <div className="space-y-1">
            {profile.selectedTTSProviders.map((providerId) => {
              const provider = ttsProviders.find((p) => p.id === providerId);
              return (
                <div key={providerId} className="flex items-center gap-1">
                  <div className="text-xs bg-purple-50 text-purple-700 px-2 py-1 rounded flex-1">
                    {provider?.displayName ?? providerId}
                  </div>
                  {profile.defaultTtsProviderId === providerId && (
                    <Badge className="text-xs bg-violet-100 text-violet-700 border-violet-200">
                      Default
                    </Badge>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {profile.selectedTemplates.length > 0 && (
        <div>
          <h4 className="text-sm font-medium text-gray-700 mb-2">
            Templates ({profile.selectedTemplates.length})
          </h4>
          <div className="grid grid-cols-4 gap-2">
            {profile.selectedTemplates.map((templateId) => {
              const template = templates.find((t) => t.id === templateId);
              return (
                <div
                  key={templateId}
                  className="text-xs bg-gray-50 text-gray-700 px-2 py-1 rounded"
                >
                  {template?.name ?? templateId}
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="flex justify-end space-x-2">
        <Button variant="outline" onClick={onClose}>
          Schließen
        </Button>
        <Button onClick={onEdit}>
          <Edit className="w-4 h-4 mr-2" />
          Bearbeiten
        </Button>
      </div>
    </div>
  );
}

// ── Edit / Create form ────────────────────────────────────────────────────────

interface EditProfileFormProps {
  formData: ProfileFormData;
  setFormData: React.Dispatch<React.SetStateAction<ProfileFormData>>;
  models: LLMModel[];
  llmGroups: LlmProviderGroup[];
  sttProviders: SpeechToTextProvider[];
  ttsProviders: TextToSpeechProvider[];
  templates: PromptTemplate[];
  subscriptionPlans: SubscriptionPlan[];
  saving: boolean;
  onToggleModel: (id: string) => void;
  onToggleStt: (id: string) => void;
  onToggleTts: (id: string) => void;
  onToggleTemplate: (id: string) => void;
  onUpdatePlatformConfig: (key: keyof ProfileFormData['platformConfig'], value: boolean) => void;
  onSave: () => void;
  onCancel: () => void;
}

function EditProfileForm({
  formData,
  setFormData,
  models,
  llmGroups,
  sttProviders,
  ttsProviders,
  templates,
  subscriptionPlans,
  saving,
  onToggleModel,
  onToggleStt,
  onToggleTts,
  onToggleTemplate,
  onUpdatePlatformConfig,
  onSave,
  onCancel,
}: EditProfileFormProps) {
  const selectedModelObjects = models.filter((m) => formData.selectedModels.includes(m.id));
  const selectedSttObjects = sttProviders.filter((p) =>
    formData.selectedSTTProviders.includes(p.id),
  );
  const selectedTtsObjects = ttsProviders.filter((p) =>
    formData.selectedTTSProviders.includes(p.id),
  );

  return (
    <div className="space-y-6">
      {/* Basic info */}
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="reg-name">Profilname *</Label>
          <Input
            id="reg-name"
            value={formData.name}
            onChange={(e) => setFormData((f) => ({ ...f, name: e.target.value }))}
            placeholder="z.B. Standard Business Profile"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="reg-plan">Abonnement Plan *</Label>
          <Select
            value={formData.subscriptionPlanId}
            onValueChange={(value) =>
              setFormData((f) => ({ ...f, subscriptionPlanId: value }))
            }
          >
            <SelectTrigger id="reg-plan">
              <SelectValue placeholder="Plan wählen" />
            </SelectTrigger>
            <SelectContent>
              {subscriptionPlans.map((plan) => (
                <SelectItem key={plan.id} value={plan.id}>
                  {plan.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="reg-desc">Beschreibung</Label>
        <Textarea
          id="reg-desc"
          value={formData.description}
          onChange={(e) => setFormData((f) => ({ ...f, description: e.target.value }))}
          placeholder="Beschreibung des Registrierungsprofils..."
          className="min-h-16"
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="reg-maxreg">Max. Registrierungen (optional)</Label>
          <Input
            id="reg-maxreg"
            type="number"
            value={formData.maxRegistrations ?? ''}
            onChange={(e) =>
              setFormData((f) => ({
                ...f,
                maxRegistrations: e.target.value ? parseInt(e.target.value) : undefined,
              }))
            }
            placeholder="Unbegrenzt"
          />
        </div>
        <div className="space-y-3 pt-6">
          <div className="flex items-center space-x-2">
            <Switch
              id="reg-active"
              checked={formData.isActive}
              onCheckedChange={(checked) => setFormData((f) => ({ ...f, isActive: checked }))}
            />
            <Label htmlFor="reg-active">Profil aktiv</Label>
          </div>
          <div className="flex items-center space-x-2">
            <Switch
              id="reg-default"
              checked={formData.isDefault}
              onCheckedChange={(checked) => setFormData((f) => ({ ...f, isDefault: checked }))}
            />
            <Label htmlFor="reg-default">Als Standard-Profil setzen</Label>
            {formData.isDefault && (
              <span className="text-xs text-yellow-600 flex items-center gap-0.5">
                <Star className="w-3 h-3" />
                Standard
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Tabs: KI-Modelle / STT / TTS / Templates / Platform */}
      <Tabs defaultValue="llm">
        <TabsList>
          <TabsTrigger value="llm">
            KI-Modelle
            {formData.selectedModels.length > 0 && (
              <Badge variant="secondary" className="ml-1 text-xs">
                {formData.selectedModels.length}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="stt">
            STT
            {formData.selectedSTTProviders.length > 0 && (
              <Badge variant="secondary" className="ml-1 text-xs">
                {formData.selectedSTTProviders.length}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="tts">
            TTS
            {formData.selectedTTSProviders.length > 0 && (
              <Badge variant="secondary" className="ml-1 text-xs">
                {formData.selectedTTSProviders.length}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="templates">
            Templates
            {formData.selectedTemplates.length > 0 && (
              <Badge variant="secondary" className="ml-1 text-xs">
                {formData.selectedTemplates.length}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="platform">Platform</TabsTrigger>
        </TabsList>

        {/* KI-Modelle */}
        <TabsContent value="llm" className="mt-3 space-y-3">
          <p className="text-xs text-gray-500">
            Checkbox = freigeschaltet (isEnabled). Radio = Default-Modell (genau eines pro Profil).
          </p>
          {llmGroups.length === 0 ? (
            <p className="text-sm text-gray-400 py-4 text-center">Keine Modelle verfügbar.</p>
          ) : (
            <div className="space-y-3 max-h-72 overflow-y-auto pr-1">
              {llmGroups.map((group) => (
                <div
                  key={group.providerId}
                  className="overflow-hidden rounded-lg border border-gray-200"
                >
                  <div className="flex items-center justify-between border-b border-gray-200 bg-gray-50 px-4 py-2">
                    <div className="flex items-center space-x-2">
                      <span className="text-sm font-semibold text-gray-800">
                        {group.providerName}
                      </span>
                      {group.dataResidency && (
                        <Badge className="text-xs font-semibold uppercase bg-blue-600 text-white border-transparent hover:bg-blue-600">
                          {group.dataResidency}
                        </Badge>
                      )}
                      <Badge variant="outline" className="text-xs">
                        {group.models.length}
                      </Badge>
                    </div>
                  </div>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-16 text-center">Default</TableHead>
                        <TableHead className="w-16 text-center">Aktiv</TableHead>
                        <TableHead>Modell</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {group.models.map((model) => {
                        const isChecked = formData.selectedModels.includes(model.id);
                        const isDefaultModel = formData.defaultModelId === model.id;
                        return (
                          <TableRow key={model.id} className="hover:bg-gray-50">
                            <TableCell className="text-center">
                              <input
                                type="radio"
                                name="default-model"
                                value={model.id}
                                checked={isDefaultModel}
                                disabled={!isChecked}
                                onChange={() =>
                                  setFormData((f) => ({ ...f, defaultModelId: model.id }))
                                }
                                className="accent-violet-600 cursor-pointer disabled:cursor-not-allowed disabled:opacity-40"
                                title={isChecked ? 'Als Default-Modell setzen' : 'Erst freischalten'}
                                aria-label={`${model.displayName} als Default setzen`}
                              />
                            </TableCell>
                            <TableCell className="text-center">
                              <Checkbox
                                id={`frm-model-${model.id}`}
                                checked={isChecked}
                                onCheckedChange={() => onToggleModel(model.id)}
                              />
                            </TableCell>
                            <TableCell>
                              <Label
                                htmlFor={`frm-model-${model.id}`}
                                className="text-sm cursor-pointer font-normal"
                              >
                                {model.displayName}
                                <span className="text-xs text-gray-400 ml-1">({model.modelKey})</span>
                              </Label>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              ))}
            </div>
          )}
          {formData.selectedModels.length > 0 && !formData.defaultModelId && (
            <p className="text-xs text-amber-600">
              Kein Default-Modell gesetzt. Wähle eines der aktivierten Modelle als Default.
            </p>
          )}
          {formData.defaultModelId && (
            <p className="text-xs text-violet-600">
              Default:{' '}
              {selectedModelObjects.find((m) => m.id === formData.defaultModelId)?.displayName}
            </p>
          )}
        </TabsContent>

        {/* STT */}
        <TabsContent value="stt" className="mt-3 space-y-3">
          <p className="text-xs text-gray-500">
            Checkbox = freigeschaltet. Radio = Default-STT-Provider.
          </p>
          {sttProviders.length === 0 ? (
            <p className="text-sm text-gray-400 py-4 text-center">Keine STT-Provider verfügbar.</p>
          ) : (
            <div className="max-h-56 overflow-y-auto pr-1 rounded-lg border border-gray-200">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-16 text-center">Default</TableHead>
                    <TableHead className="w-16 text-center">Aktiv</TableHead>
                    <TableHead>Provider</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sttProviders.map((provider) => {
                    const isChecked = formData.selectedSTTProviders.includes(provider.id);
                    const isDefaultStt = formData.defaultSttProviderId === provider.id;
                    return (
                      <TableRow key={provider.id} className="hover:bg-gray-50">
                        <TableCell className="text-center">
                          <input
                            type="radio"
                            name="default-stt"
                            value={provider.id}
                            checked={isDefaultStt}
                            disabled={!isChecked}
                            onChange={() =>
                              setFormData((f) => ({ ...f, defaultSttProviderId: provider.id }))
                            }
                            className="accent-violet-600 cursor-pointer disabled:cursor-not-allowed disabled:opacity-40"
                            title={isChecked ? 'Als Default-STT setzen' : 'Erst freischalten'}
                            aria-label={`${provider.displayName} als Default-STT setzen`}
                          />
                        </TableCell>
                        <TableCell className="text-center">
                          <Checkbox
                            id={`frm-stt-${provider.id}`}
                            checked={isChecked}
                            onCheckedChange={() => onToggleStt(provider.id)}
                          />
                        </TableCell>
                        <TableCell>
                          <Label
                            htmlFor={`frm-stt-${provider.id}`}
                            className="text-sm cursor-pointer font-normal"
                          >
                            {provider.displayName}
                            <span className="text-xs text-gray-400 ml-1">({provider.providerType})</span>
                          </Label>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
          {formData.defaultSttProviderId && (
            <p className="text-xs text-violet-600">
              Default:{' '}
              {selectedSttObjects.find((p) => p.id === formData.defaultSttProviderId)?.displayName}
            </p>
          )}
        </TabsContent>

        {/* TTS */}
        <TabsContent value="tts" className="mt-3 space-y-3">
          <p className="text-xs text-gray-500">
            Checkbox = freigeschaltet. Radio = Default-TTS-Provider.
          </p>
          {ttsProviders.length === 0 ? (
            <p className="text-sm text-gray-400 py-4 text-center">Keine TTS-Provider verfügbar.</p>
          ) : (
            <div className="max-h-56 overflow-y-auto pr-1 rounded-lg border border-gray-200">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-16 text-center">Default</TableHead>
                    <TableHead className="w-16 text-center">Aktiv</TableHead>
                    <TableHead>Provider</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {ttsProviders.map((provider) => {
                    const isChecked = formData.selectedTTSProviders.includes(provider.id);
                    const isDefaultTts = formData.defaultTtsProviderId === provider.id;
                    return (
                      <TableRow key={provider.id} className="hover:bg-gray-50">
                        <TableCell className="text-center">
                          <input
                            type="radio"
                            name="default-tts"
                            value={provider.id}
                            checked={isDefaultTts}
                            disabled={!isChecked}
                            onChange={() =>
                              setFormData((f) => ({ ...f, defaultTtsProviderId: provider.id }))
                            }
                            className="accent-violet-600 cursor-pointer disabled:cursor-not-allowed disabled:opacity-40"
                            title={isChecked ? 'Als Default-TTS setzen' : 'Erst freischalten'}
                            aria-label={`${provider.displayName} als Default-TTS setzen`}
                          />
                        </TableCell>
                        <TableCell className="text-center">
                          <Checkbox
                            id={`frm-tts-${provider.id}`}
                            checked={isChecked}
                            onCheckedChange={() => onToggleTts(provider.id)}
                          />
                        </TableCell>
                        <TableCell>
                          <Label
                            htmlFor={`frm-tts-${provider.id}`}
                            className="text-sm cursor-pointer font-normal"
                          >
                            {provider.displayName}
                            <span className="text-xs text-gray-400 ml-1">({provider.providerType})</span>
                          </Label>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
          {formData.defaultTtsProviderId && (
            <p className="text-xs text-violet-600">
              Default:{' '}
              {selectedTtsObjects.find((p) => p.id === formData.defaultTtsProviderId)?.displayName}
            </p>
          )}
        </TabsContent>

        {/* Templates */}
        <TabsContent value="templates" className="mt-3">
          {templates.length === 0 ? (
            <p className="text-sm text-gray-400 py-4 text-center">
              Keine System-Templates verfügbar.
            </p>
          ) : (
            <div className="grid grid-cols-2 gap-1 max-h-56 overflow-y-auto pr-1">
              {templates.map((template) => (
                <div
                  key={template.id}
                  className="flex items-center space-x-2 py-1 px-1 rounded hover:bg-gray-50"
                >
                  <Checkbox
                    id={`frm-tmpl-${template.id}`}
                    checked={formData.selectedTemplates.includes(template.id)}
                    onCheckedChange={() => onToggleTemplate(template.id)}
                  />
                  <Label
                    htmlFor={`frm-tmpl-${template.id}`}
                    className="text-xs cursor-pointer"
                  >
                    {template.name}
                  </Label>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Platform config */}
        <TabsContent value="platform" className="mt-3">
          <div className="grid grid-cols-3 gap-3">
            {Object.entries(formData.platformConfig).map(([key, value]) => (
              <div key={key} className="flex items-center space-x-2">
                <Switch
                  id={`platform-${key}`}
                  checked={value}
                  onCheckedChange={(checked) =>
                    onUpdatePlatformConfig(
                      key as keyof ProfileFormData['platformConfig'],
                      checked,
                    )
                  }
                />
                <Label htmlFor={`platform-${key}`} className="text-sm">
                  {key.replace(/([A-Z])/g, ' $1').replace('show', '').trim()}
                </Label>
              </div>
            ))}
          </div>
        </TabsContent>
      </Tabs>

      {/* Footer actions */}
      <div className="flex justify-end space-x-2 pt-2 border-t">
        <Button variant="outline" onClick={onCancel} disabled={saving}>
          Abbrechen
        </Button>
        <Button onClick={onSave} disabled={saving}>
          {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
          Speichern
        </Button>
      </div>
    </div>
  );
}
